/**
 * A crew's insurance and tax papers.
 *
 * Jonathon, 2026-08-27: a Documents section per crew, the coverage and expiry
 * read off the certificate when it is uploaded, and "flag crews whose
 * insurance is expired or expiring within 30 days."
 *
 * Two jobs live here, both of them rules rather than plumbing (rule 5):
 *
 *  1. **What a crew's papers add up to today** — `crewCoverage`. A date in a
 *     column is not an answer; "expired eight days ago" is.
 *  2. **What to do with what the reader returned** — `crewExtractionPatch`.
 *     The endpoint holds the API key and hands back what the model said; the
 *     dates, the flags and the refusal to keep a tax number are decided here,
 *     so the review screen and anything that follows it share one copy.
 *
 * ## The number is never kept
 *
 * A subcontractor's tax identification number is an EIN or their own Social
 * Security number. The schema has nowhere to put either, on purpose, and
 * `crewExtractionPatch` returns only which kind was seen. `redactTaxIds` is
 * the belt to that braces: anything that looks like a tax number is taken out
 * of the free text before it is stored beside the crew, because a model asked
 * for notes will sometimes helpfully include the number it just read.
 */

import { daysBetween, formatExpiryDate } from "./companyDocuments.js";

/**
 * How much notice counts as a warning rather than a surprise.
 *
 * Thirty days, which is what Jonathon asked for and shorter than the sixty a
 * company document gets (`EXPIRY_WARNING_DAYS` in `companyDocuments.ts`). The
 * difference is who renews it: our own licence is ours to plan around, and a
 * subcontractor's certificate is somebody else's to chase, which does not
 * usefully start two months out.
 */
export const CREW_EXPIRY_WARNING_DAYS = 30;

/** What kinds of paper a crew has on file. */
export const CREW_DOCUMENT_KINDS = ["coi", "w9", "license", "other"] as const;
export type CrewDocumentKind = (typeof CREW_DOCUMENT_KINDS)[number];

export const CREW_DOCUMENT_LABELS: Record<CrewDocumentKind, string> = {
  coi: "Certificate of insurance",
  w9: "W-9",
  license: "Licence",
  other: "Other",
};

/** The little of a crew that its papers depend on. */
export interface CrewPapers {
  has_wc?: boolean | null;
  wc_expires_on?: string | null;
  has_gl?: boolean | null;
  gl_expires_on?: string | null;
  has_ein?: boolean | null;
  has_social?: boolean | null;
}

export type CoverageState =
  /** Nobody has said whether this crew carries anything. */
  | "unknown"
  /** Somebody has said: they do not carry it. */
  | "uncovered"
  /** Covered, and the certificate has run out. */
  | "expired"
  /** Covered, and it runs out inside the warning window. */
  | "expiring"
  /** Covered, with a date far enough away to ignore. */
  | "covered"
  /** Covered, and no date was ever recorded. */
  | "undated";

export interface Coverage {
  state: CoverageState;
  /** The earlier of the two policies, which is the one that lapses first. */
  expiresOn: string | null;
  /** Days until that date. Negative once it has passed; null with no date. */
  days: number | null;
}

/** A date-only string, as the columns hold it. Anything else is null. */
const asDay = (value: string | null | undefined): string | null => {
  const text = (value ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(text)) return null;
  return text.slice(0, 10);
};

/**
 * Whole days from today to a date, both read as dates and not moments.
 *
 * A certificate expires on a day, not at an instant: comparing the column
 * against `new Date()` makes a policy expiring today look expired from one
 * minute past midnight. `daysBetween` in `companyDocuments.ts` already counts
 * whole days from UTC midnight, and a crew certificate is the same kind of
 * date as a company licence.
 */
export function daysUntil(day: string, today: string): number | null {
  const from = asDay(today);
  const to = asDay(day);
  if (!from || !to) return null;
  return daysBetween(from, to);
}

/**
 * Where one policy stands today.
 *
 * A crew that carries a policy with no date on it is `undated` rather than
 * covered: it is a fact somebody typed in, not a certificate anybody has seen,
 * and it is exactly the row that needs a document uploading.
 */
export function policyState(
  has: boolean | null | undefined,
  expiresOn: string | null | undefined,
  today: string,
): Coverage {
  if (has == null) return { state: "unknown", expiresOn: null, days: null };
  if (!has) return { state: "uncovered", expiresOn: null, days: null };

  const day = asDay(expiresOn);
  if (!day) return { state: "undated", expiresOn: null, days: null };

  const days = daysUntil(day, today);
  if (days === null) return { state: "undated", expiresOn: null, days: null };
  if (days < 0) return { state: "expired", expiresOn: day, days };
  if (days <= CREW_EXPIRY_WARNING_DAYS)
    return { state: "expiring", expiresOn: day, days };
  return { state: "covered", expiresOn: day, days };
}

/**
 * The worse of the two policies, which is the one worth showing.
 *
 * Workers' comp and general liability run on separate certificates with
 * separate dates, and a crew is only as covered as its weaker one — a crew
 * with current liability and lapsed comp is the company's exposure, which is
 * the whole reason these columns exist.
 */
const RANK: Record<CoverageState, number> = {
  uncovered: 0,
  expired: 1,
  expiring: 2,
  undated: 3,
  unknown: 4,
  covered: 5,
};

export function crewCoverage(crew: CrewPapers, today: string): Coverage {
  const wc = policyState(crew.has_wc, crew.wc_expires_on, today);
  const gl = policyState(crew.has_gl, crew.gl_expires_on, today);
  const worse = RANK[wc.state] <= RANK[gl.state] ? wc : gl;
  // Both dated and both fine: name the earlier date, because that is the one
  // somebody has to chase first.
  if (worse.state === "covered" && wc.expiresOn && gl.expiresOn) {
    return wc.expiresOn <= gl.expiresOn ? wc : gl;
  }
  return worse;
}

/** Whether this crew is one to chase — expired, expiring, or not covered. */
export function crewNeedsChasing(crew: CrewPapers, today: string): boolean {
  const state = crewCoverage(crew, today).state;
  return state === "expired" || state === "expiring" || state === "uncovered";
}

/** The flag in words, for a badge that has to say why. */
export function coverageLabel(coverage: Coverage): string {
  switch (coverage.state) {
    case "expired":
      return coverage.days === -1
        ? "Expired yesterday"
        : `Expired ${Math.abs(coverage.days ?? 0)} days ago`;
    case "expiring":
      return coverage.days === 0
        ? "Expires today"
        : coverage.days === 1
          ? "Expires tomorrow"
          : `Expires in ${coverage.days} days`;
    case "uncovered":
      return "Not covered";
    case "undated":
      return "No expiry on file";
    case "unknown":
      return "Not recorded";
    case "covered":
      return coverage.expiresOn
        ? `Covered to ${formatExpiryDate(coverage.expiresOn)}`
        : "Covered";
  }
}

/* --------------------------------------------------- reading a document -- */

/**
 * Anything shaped like a tax number, taken out of free text.
 *
 * An EIN is 12-3456789 and a Social Security number is 123-45-6789; both are
 * nine digits, however they are punctuated. A model asked to summarise a W-9
 * will sometimes include the number, and this is the last thing between that
 * and a database column. It is deliberately blunt: a nine-digit invoice number
 * in a note is a small loss, a Social Security number in a text column is not.
 */
export function redactTaxIds(text: string | null | undefined): string | null {
  const value = (text ?? "").trim();
  if (value === "") return null;
  return (
    value
      // 123-45-6789, 12-3456789, 123 45 6789, 123456789
      .replace(/\b\d{2,3}[-\s]?\d{2,4}[-\s]?\d{4}\b/g, "[redacted]")
      .trim() || null
  );
}

/** What the reader returned, before any of it is believed. */
export interface CrewExtraction {
  document_kind?: unknown;
  has_workers_comp?: unknown;
  workers_comp_expires_on?: unknown;
  has_general_liability?: unknown;
  general_liability_expires_on?: unknown;
  insurer?: unknown;
  tax_id_type?: unknown;
  legal_name?: unknown;
  notes?: unknown;
  confidence?: unknown;
}

/** The columns a confirmed reading writes. Only what was actually found. */
export interface CrewPatch {
  has_wc?: boolean;
  wc_expires_on?: string | null;
  has_gl?: boolean;
  gl_expires_on?: string | null;
  insurer?: string | null;
  has_ein?: boolean;
  has_social?: boolean;
}

const asBool = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

const asText = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  return value.trim() || null;
};

/**
 * Turn a reading into a patch for the crew row.
 *
 * Everything the reader was not sure of, or did not find, is simply absent —
 * a patch never writes a null over a date somebody typed in by hand. The one
 * exception is a policy the certificate positively says is not carried, which
 * is a fact and is written as `false`.
 *
 * The tax number is never in the patch, in any form. `tax_id_type` says which
 * kind was on the W-9 and that is all that survives.
 */
export function crewExtractionPatch(extraction: CrewExtraction): CrewPatch {
  const patch: CrewPatch = {};

  const wc = asBool(extraction.has_workers_comp);
  if (wc !== null) {
    patch.has_wc = wc;
    const day = asDay(asText(extraction.workers_comp_expires_on));
    if (day) patch.wc_expires_on = day;
  }

  const gl = asBool(extraction.has_general_liability);
  if (gl !== null) {
    patch.has_gl = gl;
    const day = asDay(asText(extraction.general_liability_expires_on));
    if (day) patch.gl_expires_on = day;
  }

  const insurer = redactTaxIds(asText(extraction.insurer));
  if (insurer) patch.insurer = insurer;

  const kind = (asText(extraction.tax_id_type) ?? "").toLowerCase();
  if (kind === "ein") {
    patch.has_ein = true;
    patch.has_social = false;
  } else if (kind === "ssn" || kind === "social") {
    patch.has_social = true;
    patch.has_ein = false;
  }

  return patch;
}

/** Which document kind the reader said this was, or "other". */
export function crewDocumentKind(value: unknown): CrewDocumentKind {
  const kind = (asText(value) ?? "").toLowerCase();
  return (CREW_DOCUMENT_KINDS as readonly string[]).includes(kind)
    ? (kind as CrewDocumentKind)
    : "other";
}
